<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseInfo from 'dashboard/components-next/AssignmentPolicy/components/BaseInfo.vue';
import RadioCard from 'dashboard/components-next/radioCard/RadioCard.vue';
import FairDistribution from 'dashboard/components-next/AssignmentPolicy/components/FairDistribution.vue';
import DataTable from 'dashboard/components-next/AssignmentPolicy/components/DataTable.vue';
import AddDataDropdown from 'dashboard/components-next/AssignmentPolicy/components/AddDataDropdown.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import {
  OPTIONS,
  ROUND_ROBIN,
  EARLIEST_CREATED,
  DEFAULT_FAIR_DISTRIBUTION_LIMIT,
  DEFAULT_FAIR_DISTRIBUTION_WINDOW,
} from 'dashboard/routes/dashboard/settings/assignmentPolicy/constants';

const props = defineProps({
  initialData: {
    type: Object,
    default: () => ({
      name: '',
      description: '',
      assignmentOrder: ROUND_ROBIN,
      conversationPriority: EARLIEST_CREATED,
      fairDistributionLimit: DEFAULT_FAIR_DISTRIBUTION_LIMIT,
      fairDistributionWindow: DEFAULT_FAIR_DISTRIBUTION_WINDOW,
      enableAutoAssignment: true,
      assignConversationsToAllAgents: false,
      preventWhatsappGroupAssignment: false,
      reassignOnAgentReply: false,
      reassignOnSupervisorReply: false,
      reassignConversationsOnAgentOffline: false,
      offlineReassignmentDelayMinutes: 10,
      maxAssignmentLimit: null,
    }),
  },
  mode: {
    type: String,
    required: true,
    validator: value => ['CREATE', 'EDIT'].includes(value),
  },
  policyInboxes: {
    type: Array,
    default: () => [],
  },
  inboxList: {
    type: Array,
    default: () => [],
  },
  showInboxSection: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  isInboxLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'submit',
  'addInbox',
  'deleteInbox',
  'navigateToInbox',
  'validationChange',
]);

const { t } = useI18n();

const BASE_KEY = 'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY';

const state = reactive({
  name: '',
  description: '',
  enabled: true,
  assignmentOrder: ROUND_ROBIN,
  conversationPriority: EARLIEST_CREATED,
  fairDistributionLimit: DEFAULT_FAIR_DISTRIBUTION_LIMIT,
  fairDistributionWindow: DEFAULT_FAIR_DISTRIBUTION_WINDOW,
  enableAutoAssignment: true,
  assignConversationsToAllAgents: false,
  preventWhatsappGroupAssignment: false,
  reassignOnAgentReply: false,
  reassignOnSupervisorReply: false,
  reassignConversationsOnAgentOffline: false,
  offlineReassignmentDelayMinutes: 10,
  maxAssignmentLimit: null,
});

const validationState = ref({
  isValid: false,
});

const createOption = (type, key, stateKey) => ({
  key,
  label: t(`${BASE_KEY}.FORM.${type}.${key.toUpperCase()}.LABEL`),
  description: t(`${BASE_KEY}.FORM.${type}.${key.toUpperCase()}.DESCRIPTION`),
  isActive: state[stateKey] === key,
  disabled: false,
  disabledMessage: '',
  disabledLabel: '',
});

const assignmentOrderOptions = computed(() => {
  return OPTIONS.ORDER.map(key => {
    return createOption('ASSIGNMENT_ORDER', key, 'assignmentOrder');
  });
});

const assignmentPriorityOptions = computed(() =>
  OPTIONS.PRIORITY.map(key =>
    createOption('ASSIGNMENT_PRIORITY', key, 'conversationPriority')
  )
);

const radioSections = computed(() => [
  {
    key: 'assignmentOrder',
    label: t(`${BASE_KEY}.FORM.ASSIGNMENT_ORDER.LABEL`),
    options: assignmentOrderOptions.value,
  },
  {
    key: 'conversationPriority',
    label: t(`${BASE_KEY}.FORM.ASSIGNMENT_PRIORITY.LABEL`),
    options: assignmentPriorityOptions.value,
  },
]);

const buttonLabel = computed(() =>
  t(`${BASE_KEY}.${props.mode.toUpperCase()}.${props.mode}_BUTTON`)
);

const handleValidationChange = validation => {
  validationState.value = validation;
  emit('validationChange', validation);
};

const resetForm = () => {
  Object.assign(state, {
    name: '',
    description: '',
    enabled: true,
    assignmentOrder: ROUND_ROBIN,
    conversationPriority: EARLIEST_CREATED,
    fairDistributionLimit: DEFAULT_FAIR_DISTRIBUTION_LIMIT,
    fairDistributionWindow: DEFAULT_FAIR_DISTRIBUTION_WINDOW,
    enableAutoAssignment: true,
    assignConversationsToAllAgents: false,
    preventWhatsappGroupAssignment: false,
    reassignOnAgentReply: false,
    reassignOnSupervisorReply: false,
    reassignConversationsOnAgentOffline: false,
    offlineReassignmentDelayMinutes: 10,
    maxAssignmentLimit: null,
  });
};

const handleSubmit = () => {
  emit('submit', { ...state });
};

watch(
  () => props.initialData,
  newData => {
    Object.assign(state, newData);
  },
  { immediate: true, deep: true }
);

watch(
  () => state.assignConversationsToAllAgents,
  enabled => {
    if (!enabled) return;
    state.enableAutoAssignment = false;
    state.preventWhatsappGroupAssignment = false;
    state.reassignOnAgentReply = false;
    state.reassignOnSupervisorReply = false;
    state.reassignConversationsOnAgentOffline = false;
  }
);

watch(
  () => state.enableAutoAssignment,
  enabled => {
    if (!enabled) state.reassignConversationsOnAgentOffline = false;
  }
);

watch(
  () => state.reassignOnAgentReply,
  enabled => {
    if (!enabled) state.reassignOnSupervisorReply = false;
  }
);

defineExpose({
  resetForm,
});
</script>

<template>
  <form @submit.prevent="handleSubmit">
    <div class="flex flex-col gap-4 divide-y divide-n-weak mb-4">
      <BaseInfo
        v-model:policy-name="state.name"
        v-model:description="state.description"
        :name-label="t(`${BASE_KEY}.FORM.NAME.LABEL`)"
        :name-placeholder="t(`${BASE_KEY}.FORM.NAME.PLACEHOLDER`)"
        :description-label="t(`${BASE_KEY}.FORM.DESCRIPTION.LABEL`)"
        :description-placeholder="t(`${BASE_KEY}.FORM.DESCRIPTION.PLACEHOLDER`)"
        @validation-change="handleValidationChange"
      />

      <SettingsToggleSection
        v-model="state.enabled"
        compact
        class="pt-4"
        :header="t(`${BASE_KEY}.FORM.STATUS.LABEL`)"
        :description="
          t(`${BASE_KEY}.FORM.STATUS.${state.enabled ? 'ACTIVE' : 'INACTIVE'}`)
        "
      />

      <div class="pt-4 flex flex-col gap-3">
        <div class="flex flex-col items-start gap-1 py-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('INBOX_MGMT.SETTINGS_POPUP.AGENT_ASSIGNMENT') }}
          </label>
          <p class="mb-0 text-n-slate-11 text-sm">
            {{ t('INBOX_MGMT.ASSIGNMENT.POLICY_CENTRALIZED_DESCRIPTION') }}
          </p>
        </div>

        <SettingsToggleSection
          v-model="state.assignConversationsToAllAgents"
          compact
          :header="t('INBOX_MGMT.ASSIGNMENT.ASSIGN_TO_ALL_AGENTS')"
          :description="
            t('INBOX_MGMT.ASSIGNMENT.ASSIGN_TO_ALL_AGENTS_DESCRIPTION')
          "
        />
        <SettingsToggleSection
          v-if="!state.assignConversationsToAllAgents"
          v-model="state.preventWhatsappGroupAssignment"
          compact
          :header="t('INBOX_MGMT.ASSIGNMENT.GROUPS_DO_NOT_ASSIGN')"
          :description="
            t('INBOX_MGMT.ASSIGNMENT.GROUPS_DO_NOT_ASSIGN_DESCRIPTION')
          "
        />
        <SettingsToggleSection
          v-if="!state.assignConversationsToAllAgents"
          v-model="state.reassignOnAgentReply"
          compact
          :header="t('INBOX_MGMT.ASSIGNMENT.REASSIGN_ON_AGENT_REPLY')"
          :description="
            t('INBOX_MGMT.ASSIGNMENT.REASSIGN_ON_AGENT_REPLY_DESCRIPTION')
          "
        />
        <SettingsToggleSection
          v-if="
            !state.assignConversationsToAllAgents && state.reassignOnAgentReply
          "
          v-model="state.reassignOnSupervisorReply"
          compact
          class="ltr:ml-6 rtl:mr-6"
          :header="t('INBOX_MGMT.ASSIGNMENT.REASSIGN_ON_SUPERVISOR_REPLY')"
          :description="
            t('INBOX_MGMT.ASSIGNMENT.REASSIGN_ON_SUPERVISOR_REPLY_DESCRIPTION')
          "
        />
        <SettingsToggleSection
          v-if="!state.assignConversationsToAllAgents"
          v-model="state.enableAutoAssignment"
          compact
          :header="t('INBOX_MGMT.ASSIGNMENT.ENABLE_AUTO_ASSIGNMENT')"
          :description="t('INBOX_MGMT.ASSIGNMENT.DESCRIPTION')"
        >
          <template v-if="state.enableAutoAssignment" #editor>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4">
              <woot-input
                v-model="state.maxAssignmentLimit"
                type="number"
                min="1"
                :label="t('INBOX_MGMT.AUTO_ASSIGNMENT.MAX_ASSIGNMENT_LIMIT')"
              />
            </div>
          </template>
        </SettingsToggleSection>
        <SettingsToggleSection
          v-if="
            !state.assignConversationsToAllAgents && state.enableAutoAssignment
          "
          v-model="state.reassignConversationsOnAgentOffline"
          compact
          :header="t('INBOX_MGMT.ASSIGNMENT.REASSIGN_WHEN_OFFLINE')"
          :description="
            t('INBOX_MGMT.ASSIGNMENT.REASSIGN_WHEN_OFFLINE_DESCRIPTION')
          "
        >
          <template v-if="state.reassignConversationsOnAgentOffline" #editor>
            <div class="p-4 max-w-xs">
              <woot-input
                v-model="state.offlineReassignmentDelayMinutes"
                type="number"
                min="1"
                max="1440"
                :label="t('INBOX_MGMT.ASSIGNMENT.OFFLINE_REASSIGNMENT_DELAY')"
              />
            </div>
          </template>
        </SettingsToggleSection>
      </div>

      <div
        v-if="
          !state.assignConversationsToAllAgents && state.enableAutoAssignment
        "
        class="flex flex-col items-center"
      >
        <div
          v-for="section in radioSections"
          :key="section.key"
          class="py-4 flex flex-col items-start gap-3 w-full"
        >
          <WithLabel
            :label="section.label"
            name="assignmentPolicy"
            class="w-full flex items-start flex-col gap-3"
          >
            <div class="grid grid-cols-1 xs:grid-cols-2 gap-4 w-full">
              <RadioCard
                v-for="option in section.options"
                :id="option.key"
                :key="option.key"
                :label="option.label"
                :description="option.description"
                :is-active="option.isActive"
                :disabled="option.disabled"
                :disabled-label="option.disabledLabel"
                :disabled-message="option.disabledMessage"
                @select="state[section.key] = $event"
              />
            </div>
          </WithLabel>
        </div>
      </div>

      <div
        v-if="
          !state.assignConversationsToAllAgents && state.enableAutoAssignment
        "
        class="pt-4 pb-2 flex-col flex gap-4"
      >
        <div class="flex flex-col items-start gap-1 py-1">
          <label class="text-sm font-medium text-n-slate-12 py-1">
            {{ t(`${BASE_KEY}.FORM.FAIR_DISTRIBUTION.LABEL`) }}
          </label>
          <p class="mb-0 text-n-slate-11 text-sm">
            {{ t(`${BASE_KEY}.FORM.FAIR_DISTRIBUTION.DESCRIPTION`) }}
          </p>
        </div>
        <FairDistribution
          v-model:fair-distribution-limit="state.fairDistributionLimit"
          v-model:fair-distribution-window="state.fairDistributionWindow"
          v-model:window-unit="state.windowUnit"
        />
      </div>
    </div>

    <Button
      type="submit"
      :label="buttonLabel"
      :disabled="!validationState.isValid || isLoading"
      :is-loading="isLoading"
    />

    <div
      v-if="showInboxSection"
      class="py-4 flex-col flex gap-4 border-t border-n-weak mt-6"
    >
      <div class="flex items-end gap-4 w-full justify-between">
        <div class="flex flex-col items-start gap-1 py-1">
          <label class="text-sm font-medium text-n-slate-12 py-1">
            {{ t(`${BASE_KEY}.FORM.INBOXES.LABEL`) }}
          </label>
          <p class="mb-0 text-n-slate-11 text-sm">
            {{ t(`${BASE_KEY}.FORM.INBOXES.DESCRIPTION`) }}
          </p>
        </div>
        <AddDataDropdown
          :label="t(`${BASE_KEY}.FORM.INBOXES.ADD_BUTTON`)"
          :search-placeholder="
            t(`${BASE_KEY}.FORM.INBOXES.DROPDOWN.SEARCH_PLACEHOLDER`)
          "
          :items="inboxList"
          @add="$emit('addInbox', $event)"
        />
      </div>
      <DataTable
        :items="policyInboxes"
        :is-fetching="isInboxLoading"
        :empty-state-message="t(`${BASE_KEY}.FORM.INBOXES.EMPTY_STATE`)"
        @delete="$emit('deleteInbox', $event)"
        @navigate="$emit('navigateToInbox', $event)"
      />
    </div>
  </form>
</template>
